import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { APP_CONFIG } from '@/config/app.config';
import type { SiteWithTemplate } from '@/types/template';

import { SiteActionsDropdown } from './SiteActionsDropdown';
import { SiteStatusBadge } from './SiteStatusBadge';
import { useSiteActions } from '../../hooks/useSiteActions';
import { useStuckPublishing } from '../../hooks/useStuckPublishing';
import { getSiteTitle, getSiteUrl } from './siteDisplay';

export type SiteCardProps = {
  site: SiteWithTemplate;
  // Subscription state for the delete pre-cancel path. When undefined, the
  // hook just issues a bare DELETE — the backend will 409 if a sub is in
  // fact active, and the real error will surface in a toast.
  subscription?: { hasActiveSub: boolean; cancelAtPeriodEnd: boolean };
  onDeleted?: () => void;
};

const thumbnailFor = (subdomainOrKey: string): string =>
  `https://${subdomainOrKey}.${APP_CONFIG.SITES_DOMAIN}/images/landing-site-preview-thumbnail.jpg`;

export const SiteCard: React.FC<SiteCardProps> = ({ site, subscription, onDeleted }) => {
  const navigate = useNavigate();
  const stalled = useStuckPublishing(site);

  const {
    handleDelete,
    isDeleting,
  } = useSiteActions(site.id, {
    subscription,
    onDeleted: onDeleted || (() => window.location.reload()),
  });

  const liveUrl = getSiteUrl(site);
  const title = getSiteTitle(site);

  const siteThumb = site.subdomain ? thumbnailFor(site.subdomain) : '';
  // Templates are deployed under the `template-<key>` subdomain, not `<key>`.
  const templateThumb = site.template?.templateKey
    ? thumbnailFor(`template-${site.template.templateKey}`)
    : '';
  const [imgSrc, setImgSrc] = useState(siteThumb || templateThumb);
  const [erroredOnce, setErroredOnce] = useState(false);

  const onImgError = () => {
    if (!erroredOnce && templateThumb && imgSrc !== templateThumb) {
      setErroredOnce(true);
      setImgSrc(templateThumb);
      return;
    }
    setImgSrc('');
  };

  return (
    <div className="group relative flex flex-col rounded-2xl overflow-hidden transition-transform duration-200 ease-expo-out hover:-translate-y-1">
      <button
        type="button"
        onClick={() => navigate(`/sites/${site.id}`)}
        aria-label={`Open site ${title}`}
        className="relative aspect-video bg-foreground/5 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2"
      >
        {imgSrc && (
          <img
            src={imgSrc}
            alt=""
            onError={onImgError}
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
        )}
        <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
          <div className="inline-block bg-surface rounded">
            <SiteStatusBadge
              status={site.status}
              lastError={site.lastError}
              stalled={stalled}
            />
          </div>
          {site.paymentRequired && (
            <Badge variant="warning" title="Payment required to publish">
              Payment required
            </Badge>
          )}
        </div>
      </button>
      <div className="relative flex items-center px-4 py-3 bg-foreground text-background">
        <h3 className="text-base font-medium text-background truncate flex-1 min-w-0">
          {title}
        </h3>
        <div
          className={[
            'absolute inset-y-0 right-3 flex items-center gap-2 pl-3 bg-foreground',
            'opacity-0 pointer-events-none transition-opacity duration-150',
            'group-hover:opacity-100 group-hover:pointer-events-auto',
            'group-focus-within:opacity-100 group-focus-within:pointer-events-auto',
          ].join(' ')}
        >
          {liveUrl && (
            <a
              href={liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`View ${title}`}
              title="View site"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-background hover:bg-background/10 active:bg-background/20"
            >
              <Eye className="h-4 w-4 stroke-[1.5]" />
            </a>
          )}
          <SiteActionsDropdown
            site={{
              id: site.id,
              status: site.status,
              url: liveUrl,
              subdomain: site.subdomain,
            }}
            onEdit={() => navigate(`/sites/${site.id}/edit`)}
            onDelete={handleDelete}
            triggerVariant="button"
            inverted
            isDeleting={isDeleting}
          />
        </div>
      </div>
    </div>
  );
};

export default SiteCard;
